import React from 'react';
import { Calendar, User, Clock, Tag, Share2 } from 'lucide-react';
import { BlogPost } from '@/services/blogService';
import { Button } from '@/components/ui/button';
import { ResponsiveText, ResponsiveImage, TouchButton, useResponsive } from '../BlogUtils/ResponsiveUtils';

interface BlogHeaderProps {
  post: BlogPost;
  config: {
    showSocialSharing: boolean;
  };
}

export const BlogHeader: React.FC<BlogHeaderProps> = ({ post, config }) => {
  const { isMobile, isTablet } = useResponsive();
  
  const sharePost = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // Could add toast notification here
    }
  };

  return (
    <header className={`${isMobile ? 'pt-24 pb-12' : 'pt-32 pb-16'} relative`}>
      {/* Background Elements - Reduced on mobile */}
      <div className="absolute inset-0 -z-10">
        <div className={`absolute ${isMobile ? 'top-10 left-5 w-48 h-48' : 'top-20 left-10 w-72 h-72'} bg-blue-500/20 rounded-full blur-3xl animate-pulse`}></div>
        <div className={`absolute ${isMobile ? 'bottom-10 right-5 w-64 h-64' : 'bottom-20 right-10 w-96 h-96'} bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000`}></div>
      </div>

      <div className="relative z-10">
        {/* Category Badge */}
        <div className={`${isMobile ? 'mb-4' : 'mb-6'}`}>
          <span className={`inline-flex items-center ${isMobile ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} bg-gradient-to-r from-blue-600/30 to-purple-600/30 text-blue-200 rounded-full font-medium border border-blue-500/20 backdrop-blur-xl`}>
            {post.category}
          </span>
        </div>

        {/* Title - Responsive sizing */}
        <h1 className={`${isMobile ? 'text-2xl mb-4' : isTablet ? 'text-3xl mb-6' : 'text-4xl md:text-5xl lg:text-6xl mb-8'} font-bold leading-tight`}>
          <ResponsiveText size="4xl" className="bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
            {post.title}
          </ResponsiveText>
        </h1>

        {/* Excerpt - Responsive sizing */}
        <div className={`${isMobile ? 'mb-6' : 'mb-8'} max-w-4xl`}>
          <ResponsiveText size="lg" className="text-white/70 leading-relaxed">
            {post.excerpt}
          </ResponsiveText>
        </div>

        {/* Metadata - Stack on mobile */}
        <div className={`flex ${isMobile ? 'flex-col gap-3' : 'flex-wrap items-center gap-6'} text-white/60 ${isMobile ? 'mb-6' : 'mb-8'}`}>
          <div className="flex items-center gap-2">
            <User className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
            <span className={`font-medium ${isMobile ? 'text-sm' : ''}`}>{post.author}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
            <span className={isMobile ? 'text-sm' : ''}>{new Date(post.date).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: isMobile ? 'short' : 'long', 
              day: 'numeric' 
            })}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
            <span className={isMobile ? 'text-sm' : ''}>{post.readTime}</span>
          </div>
        </div>

        {/* Tags - Responsive layout */}
        <div className={`flex flex-wrap gap-2 ${isMobile ? 'mb-6' : 'mb-8'}`}>
          {post.tags.slice(0, isMobile ? 4 : post.tags.length).map(tag => (
            <span 
              key={tag} 
              className={`inline-flex items-center gap-1.5 ${isMobile ? 'px-2.5 py-1 text-xs' : 'px-3 py-1 text-sm'} bg-white/5 text-white/70 rounded-full backdrop-blur-xl border border-white/10 hover:bg-white/10 transition-colors`}
            >
              <Tag className={`${isMobile ? 'w-2.5 h-2.5' : 'w-3 h-3'}`} />
              {tag}
            </span>
          ))}
          {isMobile && post.tags.length > 4 && (
            <span className="inline-flex items-center px-2.5 py-1 text-xs bg-white/5 text-white/50 rounded-full backdrop-blur-xl border border-white/10">
              +{post.tags.length - 4} more
            </span>
          )}
        </div>

        {/* Actions - Touch-friendly on mobile */}
        <div className="flex flex-wrap gap-4">
          {config.showSocialSharing && (
            isMobile ? (
              <TouchButton
                onClick={sharePost}
                className="border border-white/20 text-white bg-white/5 hover:bg-white/10 backdrop-blur-xl"
                size="md"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </TouchButton>
            ) : (
              <Button
                onClick={sharePost}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 backdrop-blur-xl"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            )
          )}
        </div>
      </div>

      {/* Featured Image - Responsive */}
      {post.image && (
        <div className={`${isMobile ? 'mt-8' : 'mt-12'} relative`}>
          <ResponsiveImage
            src={post.image}
            alt={post.title}
            className={`w-full ${isMobile ? 'aspect-[4/3] rounded-xl' : 'aspect-video rounded-2xl'} object-cover shadow-2xl`}
            priority={true}
          />
          <div className={`absolute inset-0 bg-gradient-to-t from-black/20 to-transparent ${isMobile ? 'rounded-xl' : 'rounded-2xl'}`}></div>
        </div>
      )}
    </header>
  );
};

export default BlogHeader;